<template>
    <ModalComponent
        :name="modalName"
        :title="modalTitle"
        @close="closeModal"
    >
        <!-- Error state -->
        <b-alert v-if="errorMessage || paymentError" variant="danger" show>
            {{ errorMessage || paymentError }}
        </b-alert>

        <!-- Step 1: Zones -->
        <div v-if="step === 'zones'">
            <div v-if="!zonesLoaded" class="text-center py-4">
                <b-spinner />
                <p class="mt-2">{{ $t('perfil_sga.cargando_zonas') }}</p>
            </div>
            <div v-else>
                <div v-if="noAvailableZones" class="text-center py-5">
                    <div class="mb-3 d-inline-flex align-items-center justify-content-center"
                         style="width: 64px; height: 64px; border-radius: 50%; background-color: #f1f3f5;"
                    >
                        <b-icon icon="exclamation-circle" font-scale="2.4" style="color: #adb5bd;" />
                    </div>
                    <h5 class="mb-2">{{ $t('perfil_sga.cambio_asiento_no_disponible_titulo') }}</h5>
                    <p class="text-muted mb-0 px-4">
                        {{ $t('perfil_sga.cambio_asiento_no_disponible_texto') }}
                    </p>
                </div>
                <template v-else>
                    <p class="text-muted mb-3">{{ $t('perfil_sga.selecciona_zona') }}</p>

                    <!-- Map + Table layout matching SeleccionarZonaComponent -->
                    <div v-if="venueSvg">
                        <b-row>
                            <b-col md="8" sm="12" id="svg">
                                <h3 class="subtitle h4 light mb-3 mb-lg-4">{{ $t('nuevo_abono.pinchando_mapa') }}:</h3>
                                <RenderSvgComponent
                                    :svg="venueSvg"
                                    :zonas="zonesForMap"
                                    @clickZona="handleZoneClick"
                                />
                            </b-col>
                            <b-col md="4" sm="12">
                                <h3 class="subtitle h4 light mb-3 mb-lg-4">{{ $t('nuevo_abono.tabla') }}:</h3>
                                <TablaSectoresComponent
                                    :zonas="zonesForTable"
                                    @clickZona="handleZoneClick"
                                />
                            </b-col>
                        </b-row>
                    </div>

                    <!-- Fallback list when no SVG -->
                    <div v-else class="list-group">
                        <button
                            v-for="zone in zonesForList"
                            :key="zone.id"
                            class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                            :class="{ disabled: !seatChange.isZoneAvailable(zone) }"
                            :disabled="!seatChange.isZoneAvailable(zone)"
                            @click="handleZoneClick(zone)"
                        >
                            <span>{{ zone.name || zone.nombre }}</span>
                            <b-badge :variant="seatChange.isZoneAvailable(zone) ? 'success' : 'secondary'">
                                {{ seatChange.isZoneAvailable(zone) ? $t('disponible') : $t('ocupado') }}
                            </b-badge>
                        </button>
                    </div>

                    <div v-if="zonesForList.length === 0" class="text-center py-4 text-muted">
                        {{ $t('perfil_sga.sin_zonas_disponibles') }}
                    </div>
                </template>
            </div>
        </div>

        <!-- Step 2: Seats -->
        <div v-if="step === 'seats'">
            <div class="d-flex justify-content-between align-items-center mb-3">
                <b-button variant="outline-secondary" size="sm" @click="goBackToZones">
                    <b-icon icon="arrow-left" /> {{ $t('atras') }}
                </b-button>
                <span class="font-weight-bold">{{ selectedZone?.name || selectedZone?.nombre }}</span>
            </div>
            <div v-if="loading" class="text-center py-4">
                <b-spinner />
                <p class="mt-2">{{ $t('perfil_sga.cargando_asientos') }}</p>
            </div>
            <div v-else>
                <SeatMapSga
                    v-if="hasSeats"
                    :loading="false"
                    :print_align="'center'"
                    :filas="seats"
                    @asientoSeleccionado="handleSeatClick"
                />
                <b-alert v-else variant="warning" show>
                    {{ $t('perfil_sga.sin_butacas_disponibles') }}
                </b-alert>

                <!-- Selected seat info and price difference -->
                <div v-if="validatingSeat" class="text-center py-2 mt-3 text-muted">
                    <b-spinner small class="mr-2" />
                    {{ $t('perfil_sga.calculando_precio') }}
                </div>
                <div v-else-if="selectedSeat && priceDiff" class="bg-light rounded p-3 mt-3">
                    <div class="d-flex justify-content-between align-items-center">
                        <div>
                            <p class="mb-0 text-muted small">{{ $t('perfil_sga.butaca_nueva') }}:
                                <strong>{{ newSeatLabel }}</strong>
                                <span v-if="selectedZone" class="ml-2">· {{ selectedZone.name || selectedZone.nombre }}</span>
                            </p>
                        </div>
                        <div class="text-right">
                            <span class="text-muted small">{{ $t('perfil_sga.diferencia_precio') }}:</span>
                            <span class="d-block font-weight-bold" :class="priceDiffClass">{{ formattedPriceDiff }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Step 3: Confirmation / Payment -->
        <div v-if="step === 'confirmation'">
            <div v-if="paymentState === 'selection'">
                <div class="d-flex justify-content-between align-items-center mb-3">
                    <b-button variant="outline-secondary" size="sm" @click="goBackToSeats">
                        <b-icon icon="arrow-left" /> {{ $t('atras') }}
                    </b-button>
                </div>
                <div v-if="priceDiff" class="mb-4">
                    <!-- Resumen header -->
                    <h5 class="mb-4 font-weight-normal d-flex align-items-center">
                        <b-icon icon="clipboard-check" font-scale="1.1" class="mr-2" style="color: #6c8eb5;" />
                        {{ $t('perfil_sga.resumen_cambio') }}
                    </h5>

                    <!-- Seat comparison -->
                    <div class="row align-items-stretch text-center">
                        <!-- Old seat -->
                        <div class="col-5">
                            <div class="h-100 py-3 px-2"
                                 style="border: 1px solid #dee2e6; border-radius: 4px;"
                            >
                                <div class="mb-2"
                                     style="color: #adb5bd; font-size: 1.5rem;"
                                >
                                    <b-icon icon="geo-alt" />
                                </div>
                                <div class="text-muted small text-uppercase mb-2"
                                     style="letter-spacing: 1px; font-size: 0.7rem; font-weight: 600;"
                                >
                                    {{ $t('perfil_sga.butaca_actual') }}
                                </div>
                                <div class="mb-1" style="font-size: 1rem; font-weight: 500;">
                                    {{ currentZoneName }}
                                </div>
                                <div class="text-muted small">
                                    {{ $t('perfil_sga.fila') }} {{ props.abono?.asiento?.fila || '-' }}
                                    · {{ $t('perfil_sga.asiento') }} {{ props.abono?.asiento?.asiento || '-' }}
                                </div>
                            </div>
                        </div>

                        <!-- Arrow -->
                        <div class="col-2 d-flex align-items-center justify-content-center">
                            <div class="rounded-circle d-flex align-items-center justify-content-center"
                                 style="width: 36px; height: 36px; background-color: #f1f3f5; color: #6c8eb5;"
                            >
                                <b-icon icon="arrow-right" font-scale="1.3" />
                            </div>
                        </div>

                        <!-- New seat -->
                        <div class="col-5">
                            <div class="h-100 py-3 px-2"
                                 style="border: 1px solid #adb5bd; border-radius: 4px;"
                            >
                                <div class="mb-2"
                                     style="color: #6c8eb5; font-size: 1.5rem;"
                                >
                                    <b-icon icon="pin-map-fill" />
                                </div>
                                <div class="text-dark small text-uppercase mb-2"
                                     style="letter-spacing: 1px; font-size: 0.7rem; font-weight: 600;"
                                >
                                    {{ $t('perfil_sga.butaca_nueva') }}
                                </div>
                                <div class="mb-1" style="font-size: 1rem; font-weight: 500;">
                                    {{ newZoneName }}
                                </div>
                                <div class="text-muted small">
                                    {{ $t('perfil_sga.fila') }} {{ selectedSeatInfo?.fila || '-' }}
                                    · {{ $t('perfil_sga.asiento') }} {{ selectedSeatInfo?.asiento || '-' }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <hr class="my-4"/>

                    <!-- Price breakdown -->
                    <div class="d-flex justify-content-between align-items-center py-2">
                        <span class="text-muted d-flex align-items-center">
                            <b-icon icon="cash-stack" font-scale="0.9" class="mr-2" style="color: #adb5bd;" />
                            {{ $t('perfil_sga.diferencia_precio') }}
                        </span>
                        <span :class="priceDiffClass">{{ formattedPriceDiff }}</span>
                    </div>
                    <div v-if="hasPriceManagement" class="d-flex justify-content-between align-items-center py-2">
                        <span class="text-muted d-flex align-items-center">
                            <b-icon icon="receipt" font-scale="0.9" class="mr-2" style="color: #adb5bd;" />
                            {{ $t('perfil_sga.gastos_gestion') }}
                        </span>
                        <span>+{{ formattedPriceManagement }}</span>
                    </div>

                    <div class="d-flex justify-content-between align-items-center pt-3 mt-2"
                         style="border-top: 1px solid #dee2e6;"
                    >
                        <span class="font-weight-bold d-flex align-items-center">
                            <b-icon icon="wallet2" font-scale="1" class="mr-2" style="color: #6c8eb5;" />
                            {{ $t('perfil_sga.total') }}
                        </span>
                        <span class="font-weight-bold" style="font-size: 1.25rem;">
                            {{ formattedTotalWithManagement }}
                        </span>
                    </div>
                </div>
            </div>

            <div v-if="paymentState === 'payment'">
                <b-alert v-if="paymentError" variant="danger" show>
                    {{ paymentError }}
                </b-alert>

                <div v-if="!paymentProcessing && !paymentChallengeUrl">
                    <div class="bg-light rounded p-4 mb-4">
                        <div class="d-flex justify-content-between py-2">
                            <span class="text-muted">{{ $t('perfil_sga.diferencia_precio') }}</span>
                            <span :class="priceDiffClass">{{ formattedPriceDiff }}</span>
                        </div>
                        <div v-if="hasPaymentManagement" class="d-flex justify-content-between py-2">
                            <span class="text-muted">{{ $t('perfil_sga.gastos_gestion') }}</span>
                            <span class="font-weight-medium">+{{ formattedPaymentManagement }}</span>
                        </div>
                        <div class="d-flex justify-content-between py-2 font-weight-bold border-top mt-2">
                            <span>{{ $t('perfil_sga.total_a_pagar') }}</span>
                            <span class="text-primary">{{ formattedTotalPrice }}</span>
                        </div>
                    </div>
                    <FormularioPagoComponent
                        :jet_id="paymentData.jet_id"
                        @payToken="handlePayToken"
                    />
                </div>

                <div v-else-if="paymentChallengeUrl" class="text-center">
                    <p class="mb-3">{{ $t('perfil_sga.procesando_pago') }}</p>
                    <iframe
                        class="shadow my-2 bg-white"
                        style="display: block; margin: 0 auto; overflow-y: scroll; overflow-x: hidden;"
                        title="3D Secure"
                        sandbox="allow-top-navigation allow-scripts allow-same-origin allow-forms"
                        :src="paymentChallengeUrl"
                        width="325"
                        height="523"
                        frameborder="0"
                        scrolling="yes"
                        loading="lazy"
                    ></iframe>
                </div>

                <div v-else class="text-center py-4">
                    <b-spinner label="Procesando..." />
                    <p class="mt-2">{{ $t('perfil_sga.procesando_pago') }}</p>
                </div>
            </div>

            <div v-if="paymentState === 'success'" class="py-3">
                <!-- Success header -->
                <div class="text-center mb-4">
                    <div class="d-inline-flex align-items-center justify-content-center mb-3"
                         style="width: 64px; height: 64px; border-radius: 50%; background-color: #d4edda; animation: successPop 0.5s ease;"
                    >
                        <b-icon icon="check2-circle" font-scale="2.8" style="color: #5a8f6a;" />
                    </div>
                    <h3 class="mb-1" style="color: #5a8f6a; font-weight: 500;">{{ $t('perfil_sga.cambio_exitoso') }}</h3>
                    <p class="text-muted mb-0">{{ $t('perfil_sga.cambio_confirmado') }}</p>
                    <div v-if="successData?.tpv_order" class="mt-3">
                        <div class="text-muted small text-uppercase mb-1"
                             style="letter-spacing: 1px; font-size: 0.7rem; font-weight: 600;"
                        >
                            {{ $t('gracias.n_pedido') }}
                        </div>
                        <div style="font-size: 1.75rem; font-weight: 600; color: #5a8f6a; letter-spacing: 1px;">
                            {{ successData.tpv_order }}
                        </div>
                    </div>
                </div>

                <div v-if="successData" class="mb-4">
                    <!-- Abonado -->
                    <div class="d-flex align-items-center mb-4 pb-3" style="border-bottom: 1px solid #dee2e6;">
                        <div class="mr-3 flex-shrink-0" style="color: #6c8eb5; font-size: 2rem;">
                            <b-icon icon="person-circle" />
                        </div>
                        <div class="flex-grow-1 min-width-0">
                            <div class="font-weight-medium text-truncate" style="font-size: 1.05rem;">
                                {{ summaryAbonado.nombre }}
                            </div>
                            <div v-if="summaryAbonado.dni !== '-'" class="d-flex align-items-center mt-1 small text-muted">
                                <b-icon icon="credit-card" font-scale="0.8" class="mr-1" />
                                {{ summaryAbonado.dni }}
                            </div>
                        </div>
                    </div>

                    <!-- Seat change comparison -->
                    <div class="row align-items-stretch text-center mb-4">
                        <!-- Old seat -->
                        <div class="col-5">
                            <div class="h-100 py-3 px-2"
                                 style="border: 1px solid #dee2e6; border-radius: 4px;"
                            >
                                <div class="mb-2"
                                     style="color: #adb5bd; font-size: 1.5rem;"
                                >
                                    <b-icon icon="geo-alt" />
                                </div>
                                <div class="text-muted small text-uppercase mb-2"
                                     style="letter-spacing: 1px; font-size: 0.7rem; font-weight: 600;"
                                >
                                    {{ $t('perfil_sga.butaca_actual') }}
                                </div>
                                <div class="mb-1" style="font-size: 1rem; font-weight: 500;">
                                    {{ summaryOldSeat.zona }}
                                </div>
                                <div class="text-muted small">
                                    {{ $t('perfil_sga.fila') }} {{ summaryOldSeat.fila }}
                                    · {{ $t('perfil_sga.asiento') }} {{ summaryOldSeat.asiento }}
                                </div>
                            </div>
                        </div>

                        <!-- Arrow -->
                        <div class="col-2 d-flex align-items-center justify-content-center">
                            <div class="rounded-circle d-flex align-items-center justify-content-center"
                                 style="width: 36px; height: 36px; background-color: #d4edda; color: #5a8f6a;"
                            >
                                <b-icon icon="arrow-right" font-scale="1.3" />
                            </div>
                        </div>

                        <!-- New seat -->
                        <div class="col-5">
                            <div class="h-100 py-3 px-2"
                                 style="border: 1px solid #a3c9b1; border-radius: 4px;"
                            >
                                <div class="mb-2"
                                     style="color: #5a8f6a; font-size: 1.5rem;"
                                >
                                    <b-icon icon="pin-map-fill" />
                                </div>
                                <div class="small text-uppercase mb-2"
                                     style="letter-spacing: 1px; font-size: 0.7rem; font-weight: 600; color: #5a8f6a;"
                                >
                                    {{ $t('perfil_sga.butaca_nueva') }}
                                </div>
                                <div class="mb-1" style="font-size: 1rem; font-weight: 500;">
                                    {{ summaryNewSeat.zona }}
                                </div>
                                <div class="text-muted small">
                                    {{ $t('perfil_sga.fila') }} {{ summaryNewSeat.fila }}
                                    · {{ $t('perfil_sga.asiento') }} {{ summaryNewSeat.asiento }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Price breakdown -->
                    <div class="d-flex justify-content-between align-items-center py-2">
                        <span class="text-muted d-flex align-items-center">
                            <b-icon icon="cash-stack" font-scale="0.9" class="mr-2" style="color: #adb5bd;" />
                            {{ $t('perfil_sga.diferencia_precio') }}
                        </span>
                        <span>{{ summaryImporte || '0,00 €' }}</span>
                    </div>
                    <div v-if="summaryPriceManagement" class="d-flex justify-content-between align-items-center py-2">
                        <span class="text-muted d-flex align-items-center">
                            <b-icon icon="receipt" font-scale="0.9" class="mr-2" style="color: #adb5bd;" />
                            {{ $t('perfil_sga.gastos_gestion') }}
                        </span>
                        <span>+{{ summaryPriceManagement }}</span>
                    </div>

                    <div class="d-flex justify-content-between align-items-center pt-3 mt-2"
                         style="border-top: 1px solid #dee2e6;"
                    >
                        <span class="font-weight-bold d-flex align-items-center">
                            <b-icon icon="wallet2" font-scale="1" class="mr-2" style="color: #6c8eb5;" />
                            {{ $t('perfil_sga.importe_total') }}
                        </span>
                        <span class="font-weight-bold" style="font-size: 1.25rem;">
                            {{ summaryImporteTotal || '0,00 €' }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Hidden container for Redsys form injection -->
        <div ref="redsysFormContainer" style="display: none;"></div>

        <!-- Footer -->
        <template #footer>
            <template v-if="step === 'zones'">
                <b-button variant="secondary" @click="closeModal">
                    {{ $t('cancelar') }}
                </b-button>
            </template>
            <template v-if="step === 'seats'">
                <b-button variant="outline-secondary" class="mr-2" @click="goBackToZones">
                    {{ $t('atras') }}
                </b-button>
                <b-button variant="primary" :disabled="!selectedSeat || loading" @click="confirmSeatSelection">
                    {{ $t('continuar') }}
                </b-button>
            </template>
            <template v-if="step === 'confirmation' && paymentState === 'selection'">
                <b-button variant="outline-secondary" class="mr-2" @click="goBackToSeats">
                    {{ $t('atras') }}
                </b-button>
                <b-button variant="primary" :disabled="loading" @click="confirmAndPay">
                    <b-spinner v-if="loading" small class="mr-1" />
                    {{ requiresPayment ? $t('perfil_sga.realizar_pago') : $t('perfil_sga.confirmar_cambio') }}
                </b-button>
            </template>
            <template v-if="step === 'confirmation' && paymentState === 'payment'">
                <b-button
                    variant="secondary"
                    :disabled="paymentProcessing || loading"
                    @click="cancelPayment"
                >
                    <b-spinner v-if="loading" small class="mr-1" />
                    {{ $t('formulario_pago.cancelar') }}
                </b-button>
            </template>
            <template v-if="step === 'confirmation' && paymentState === 'success'">
                <b-button variant="primary" @click="closeModalSuccess">
                    {{ $t('perfil_sga.cerrar') }}
                </b-button>
            </template>
        </template>
    </ModalComponent>
</template>

<script setup>
    import { ref, computed, onMounted, onUnmounted, getCurrentInstance } from 'vue';
    import ModalComponent from '@/components/backend/components/ModalComponent';
    import RenderSvgComponent from '@/components/common/svg/normal/RenderSvgComponent';
    import TablaSectoresComponent from '@/components/frontend/abonos/nuevo/TablaSectoresComponent';
    import SeatMapSga from '@/components/common/Asientos/SeatMapSga';
    import FormularioPagoComponent from '@/components/frontend/compra/FormularioPagoComponent';
    import useSgaCambioAsiento from '@/composables/front/sga/useSgaCambioAsiento';

    const props = defineProps({
        abonadoId: {
            type: String,
            required: true
        },
        sgaEventId: {
            type: String,
            default: null
        },
        abono: {
            type: Object,
            default: null
        }
    });

    const emit = defineEmits(['close', 'success']);

    const instance = getCurrentInstance();
    const $store = instance.proxy.$store;
    const $config = instance.proxy.config;
    const $toast = instance.proxy.$toast;
    const $t = instance.proxy.$t.bind(instance.proxy);

    // Composable for seat change business logic
    // config is imported directly inside the composable to avoid Vue 2.7 prototype lookup issues
    const seatChange = useSgaCambioAsiento($store, $t);

    // Zone loading state (separate from seatChange composable)
    const zonesLoaded = ref(false);

    // Price validation loading (keeps the seat map visible)
    const validatingSeat = ref(false);

    // Track the last clicked asiento object for visual selection toggle on the map
    let lastSelectedAsiento = null;

    // Payment state machine
    const paymentState = ref('selection'); // 'selection' | 'payment' | 'success'
    const paymentData = ref({
        jet_id: null,
        session_id: null,
        cart_id: null,
        total_price: null,
        tpv_order: null,
        price_management: 0
    });
    const paymentError = ref(null);
    const paymentProcessing = ref(false);
    const paymentChallengeUrl = ref(null);
    const paymentCheckInterval = ref(null);
    const successData = ref(null);
    const redsysFormContainer = ref(null);

    // Computed
    const modalName = computed(() => 'cambio-asiento-sga-modal-' + props.abonadoId);
    const modalTitle = computed(() => $t('perfil_sga.cambio_asiento_titulo'));

    const zonesForMap = computed(() => {
        // For RenderSvgComponent: mark unavailable zones
        return seatChange.zones.value.map(zone => ({
            ...zone,
            esta_disponible: seatChange.isZoneAvailable(zone)
        }));
    });

    const zonesForTable = computed(() => {
        // For TablaSectoresComponent: only available zones
        return seatChange.zones.value.filter(zone => seatChange.isZoneAvailable(zone));
    });

    const zonesForList = computed(() => {
        // For fallback list view
        return seatChange.zones.value;
    });

    const noAvailableZones = computed(() => {
        // When precios endpoint returns no available zones for this abonado,
        // seat change is not possible (not in window, ineligible type, etc.).
        return zonesLoaded.value && seatChange.availableZoneIds.value.size === 0;
    });

    const hasSeats = computed(() => {
        if (Array.isArray(seatChange.seats.value)) {
            return seatChange.seats.value.length > 0;
        }
        if (typeof seatChange.seats.value === 'object' && seatChange.seats.value !== null) {
            return Object.keys(seatChange.seats.value).length > 0;
        }
        return false;
    });

    const currentZoneName = computed(() => {
        return props.abono?.asiento?.region?.nombre
            || props.abono?.asiento?.zona_nombre
            || props.abono?.asiento?.zona
            || '-';
    });

    const newZoneName = computed(() => {
        return seatChange.selectedZone.value?.name || seatChange.selectedZone.value?.nombre || '-';
    });

    const currentSeatLabel = computed(() => {
        const seat = props.abono?.asiento;
        if (!seat) return '-';
        const fila = seat.fila || seat.row;
        const asiento = seat.asiento || seat.seat;
        if (fila && asiento) return `${$t('perfil_sga.fila')}: ${fila}, ${$t('perfil_sga.asiento')}: ${asiento}`;
        if (fila) return `${$t('perfil_sga.fila')}: ${fila}`;
        if (asiento) return `${$t('perfil_sga.asiento')}: ${asiento}`;
        return '-';
    });

    const newSeatLabel = computed(() => {
        const seat = seatChange.selectedSeatInfo.value || seatChange.selectedSeat.value;
        if (!seat) return '-';
        if (typeof seat === 'string') return seat;
        return `${$t('perfil_sga.fila')}: ${seat.fila || seat.row}, ${$t('perfil_sga.asiento')}: ${seat.asiento || seat.seat || seat.id}`;
    });

    const formattedPriceDiff = computed(() => {
        const diff = seatChange.priceDiff.value;
        if (!diff || typeof diff.amount !== 'number') return '-';
        const formatter = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' });
        return formatter.format(diff.amount);
    });

    const priceDiffClass = computed(() => {
        const diff = seatChange.priceDiff.value;
        if (!diff || typeof diff.amount !== 'number') return '';
        if (diff.amount > 0) return 'text-danger';
        if (diff.amount < 0) return 'text-success';
        return 'text-muted';
    });

    const formattedTotalPrice = computed(() => {
        const total = paymentData.value.total_price;
        if (!total) return '-';
        const formatter = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' });
        return formatter.format(total);
    });

    const requiresPayment = computed(() => {
        const diff = seatChange.priceDiff.value;
        return diff && typeof diff.amount === 'number' && diff.amount > 0;
    });

    const hasPriceManagement = computed(() => {
        const diff = seatChange.priceDiff.value;
        return diff && typeof diff.priceManagement === 'number' && diff.priceManagement > 0;
    });

    const formattedPriceManagement = computed(() => {
        const diff = seatChange.priceDiff.value;
        if (!diff || typeof diff.priceManagement !== 'number') return '-';
        const formatter = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' });
        return formatter.format(diff.priceManagement);
    });

    const formattedTotalWithManagement = computed(() => {
        const diff = seatChange.priceDiff.value;
        if (!diff || typeof diff.amount !== 'number') return '-';
        const total = diff.amount + (diff.priceManagement || 0);
        const formatter = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' });
        return formatter.format(total);
    });

    const hasPaymentManagement = computed(() => {
        return paymentData.value.price_management > 0;
    });

    const formattedPaymentManagement = computed(() => {
        const pm = paymentData.value.price_management;
        if (typeof pm !== 'number' || pm <= 0) return '-';
        const formatter = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' });
        return formatter.format(pm);
    });

    // Success summary helpers
    const eurFormatter = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' });
    const formatEur = value => {
        if (value === null || value === undefined || value === '') return null;
        const n = Number(value);
        if (Number.isNaN(n)) return null;
        return eurFormatter.format(n);
    };

    const summaryAbonado = computed(() => {
        const a = successData.value?.abonado || {};
        const fullName = [a.nombre, a.apellidos].filter(Boolean).join(' ').trim();
        return {
            nombre: fullName || props.abono?.nombre || '-',
            dni: a.dni || '-',
            email: a.email || '-',
            lista: a.lista?.nombre || '-'
        };
    });

    const summaryOldSeat = computed(() => {
        const seat = props.abono?.asiento;
        if (!seat) return { zona: '-', fila: '-', asiento: '-' };
        return {
            zona: seat.region?.nombre
                || seat.zona_nombre
                || seat.zona
                || '-',
            fila: seat.fila || '-',
            asiento: seat.asiento || '-'
        };
    });

    const summaryNewSeat = computed(() => {
        const s = successData.value?.abonado?.asiento_nuevo || {};
        return {
            zona: s.zona || s.zona_nombre || '-',
            fila: s.fila || '-',
            asiento: s.asiento || '-'
        };
    });

    const summaryImporte = computed(() => formatEur(successData.value?.importe));
    const summaryPriceManagement = computed(() => {
        const v = Number(successData.value?.price_management);
        if (!v || v <= 0) return null;
        return eurFormatter.format(v);
    });
    const summaryImporteTotal = computed(() => formatEur(successData.value?.importe_total));

    // Methods
    const handleZoneClick = async zone => {
        await seatChange.selectZone(props.sgaEventId, zone);
    };

    const handleSeatClick = async asiento => {
        // Defensive guard: even though SeatMapSga + AsientoComponent block the click
        // on unavailable seats, never trust the UI. If somehow an unavailable seat
        // reaches here, do NOT call the validation API nor mark it selected.
        //
        // Este flujo es Front, donde sólo se aceptan butacas libres (0): la
        // capacidad de actuar sobre bloqueos (3) es del gestor Admin y se declara
        // ahí con `allowBlocked`. Pendientes (2), reservadas y ocultas quedan fuera.
        if (
            asiento?.ocupado === 1
            || asiento?.ocupado === true
            || asiento?.ocupado === 2
            || asiento?.ocupado === 3
            || asiento?.info?.reserva_id
            || (asiento?.oculto && asiento.oculto > 0)
        ) {
            return;
        }

        // Visual toggle on the seat map: unselect previous, select current
        if (lastSelectedAsiento) {
            lastSelectedAsiento.seleccionado = false;
        }
        asiento.seleccionado = true;
        lastSelectedAsiento = asiento;

        validatingSeat.value = true;
        try {
            await seatChange.validateSeat(props.abonadoId, asiento.id || asiento.seat_id, asiento);
        } finally {
            validatingSeat.value = false;
        }
    };

    const confirmSeatSelection = () => {
        seatChange.confirmSeatSelection();
    };

    const goBackToZones = () => {
        if (lastSelectedAsiento) {
            lastSelectedAsiento.seleccionado = false;
        }
        lastSelectedAsiento = null;
        seatChange.goBackToZones();
    };

    const goBackToSeats = () => {
        if (lastSelectedAsiento) {
            lastSelectedAsiento.seleccionado = false;
        }
        lastSelectedAsiento = null;
        seatChange.goBackToSeats();
    };

    const confirmAndPay = async() => {
        seatChange.loading.value = true;
        seatChange.error.value = null;

        try {
            const cart = await seatChange.createCart(props.abonadoId, seatChange.selectedSeat.value);

            if (!cart) {
                throw new Error('No cart data received');
            }

            // Route to payment based on response
            if (cart.formulario) {
                handleRedsysPayment(cart.formulario, cart.session_id, cart.cart_id, cart.tpv_order);
            } else if (cart.jet_id) {
                handlePaycometPayment(cart.jet_id, cart.session_id, cart.cart_id, cart.total_price, cart.tpv_order, cart.price_management);
            } else {
                // No payment required - success
                paymentState.value = 'success';
                successData.value = cart;
            }
        } catch (err) {
            paymentError.value = $t('perfil_sga.error_crear_carrito');
        } finally {
            seatChange.loading.value = false;
        }
    };

    const handlePaycometPayment = (jet_id, session_id, cart_id, total_price, tpv_order, price_management) => {
        paymentState.value = 'payment';
        paymentData.value = { jet_id, session_id, cart_id, total_price, tpv_order, price_management };
        paymentError.value = null;
    };

    const handleRedsysPayment = (formulario, session_id, cart_id, tpv_order) => {
        paymentData.value = { ...paymentData.value, session_id, cart_id, tpv_order };

        if (redsysFormContainer.value) {
            redsysFormContainer.value.innerHTML = formulario;
            setTimeout(() => {
                const form = redsysFormContainer.value.querySelector('form');
                if (form) {
                    form.submit();
                } else {
                    paymentError.value = $t('perfil_sga.error_renovacion');
                }
            }, 100);
        }
    };

    const handlePayToken = jetToken => {
        paymentProcessing.value = true;
        paymentError.value = null;

        const params = {
            filters: {},
            session_id: paymentData.value.session_id,
            jetToken: jetToken,
            tipo_venta: 'sga'
        };

        $store.dispatch($config.getVuexAlias('front.procesar-pago-paycomet'), params)
            .then(res => {
                if (res.data?.order) {
                    paymentData.value.tpv_order = res.data.order;
                }
                if (res.data.response.challengeUrl) {
                    paymentChallengeUrl.value = res.data.response.challengeUrl;
                    startPaymentCheck();
                } else if (res.data.response.compraFinalizada) {
                    completePayment();
                }
            })
            .catch(error => {
                paymentProcessing.value = false;
                stopPaymentCheck();
                const errorCode = error.response?.data?.response?.errorCode;
                const errorMap = {
                    102: 'Operación no permitida por tipo de tarjeta',
                    105: 'Crédito insuficiente',
                    108: 'Error en el número de tarjeta',
                    109: 'Error en la fecha de caducidad',
                    110: 'Error en los datos',
                    111: 'Bloque CVC2 incorrecto',
                    133: 'Error al procesar el pago, inténtelo de nuevo'
                };
                paymentError.value = errorMap[errorCode] || $t('perfil_sga.error_pago');
            });
    };

    const startPaymentCheck = () => {
        paymentCheckInterval.value = setInterval(() => {
            checkPaymentCompletion();
        }, 3000);
    };

    const stopPaymentCheck = () => {
        if (paymentCheckInterval.value) {
            clearInterval(paymentCheckInterval.value);
            paymentCheckInterval.value = null;
        }
    };

    const checkPaymentCompletion = () => {
        const params = {
            filters: {},
            session_id: paymentData.value.session_id,
            tipo_pedido: 'sga'
        };

        $store.dispatch($config.getVuexAlias('front.get-pedido-procesado-paycomet'), params)
            .then(res => {
                if (res.data?.n_pedido) {
                    paymentData.value.tpv_order = res.data.n_pedido;
                }
                if (!res.data.error) {
                    stopPaymentCheck();
                    completePayment();
                }
            })
            .catch(() => {
                // Ignore errors, keep polling
            });
    };

    const completePayment = () => {
        stopPaymentCheck();
        seatChange.loading.value = true;

        const params = {
            filters: {},
            session_id: paymentData.value.session_id,
            tpv_order: paymentData.value.tpv_order
        };

        $store.dispatch($config.getVuexAlias('sga.cart.resumen'), params)
            .then(res => {
                successData.value = res.data?.data || null;
                paymentState.value = 'success';
            })
            .catch(() => {
                successData.value = {
                    tipo: 'cambio-asiento',
                    tpv_order: paymentData.value.tpv_order
                };
                paymentState.value = 'success';
            })
            .finally(() => {
                seatChange.loading.value = false;
            });
    };

    const cancelPayment = () => {
        seatChange.loading.value = true;

        $store.dispatch(
            $config.getVuexAlias('sga.cart.eliminar-datos'),
            { cart_id: paymentData.value.cart_id }
        )
            .then(() => {
                $toast.success($t('perfil_sga.carrito_cancelado'));
            })
            .catch(() => {
                // Even if delete fails, proceed
            })
            .finally(() => {
                seatChange.loading.value = false;
                closeModal();
            });
    };

    const closeModal = () => {
        stopPaymentCheck();
        seatChange.reset();
        paymentState.value = 'selection';
        paymentData.value = { jet_id: null, session_id: null, cart_id: null, total_price: null, tpv_order: null, price_management: 0 };
        paymentError.value = null;
        paymentProcessing.value = false;
        paymentChallengeUrl.value = null;
        successData.value = null;
        zonesLoaded.value = false;
        emit('close');
    };

    const closeModalSuccess = () => {
        emit('success', successData.value);
        closeModal();
    };

    /**
     * Load venue zones from sga.get.zonas endpoint.
     * Matches the pattern used in SeleccionarZonaComponent.
     */
    const loadZones = async() => {
        try {
            const response = await $store.dispatch(
                $config.getVuexAlias('sga.get.zonas'),
                { event_id: props.sgaEventId }
            );

            seatChange.zones.value = response.data?.zonas || [];
            seatChange.venueSvg.value = response.data?.svg || null;
        } catch (e) {
            console.error('[CambioAsientoSgaModal] Error loading zones:', e);
            $toast.error($t('perfil_sga.error_cargar_zonas'));
        }
    };

    /**
     * On mount, load zones from sga.get.zonas and available zones from precios.
     * The component mounts via v-if in the parent each time the modal opens.
     */
    onMounted(async() => {
        seatChange.reset();
        paymentState.value = 'selection';
        zonesLoaded.value = false;

        const promises = [];

        if (props.sgaEventId) {
            promises.push(loadZones());
        } else {
            console.warn('[CambioAsientoSgaModal] No sgaEventId provided, zone map unavailable');
        }

        promises.push(seatChange.fetchPrecios(props.abonadoId));

        await Promise.all(promises);
        zonesLoaded.value = true;
    });

    // Cleanup on unmount
    onUnmounted(() => {
        stopPaymentCheck();
    });

    // Expose step from composable
    const step = seatChange.step;
    const zones = seatChange.zones;
    const venueSvg = seatChange.venueSvg;
    const selectedZone = seatChange.selectedZone;
    const seats = seatChange.seats;
    const selectedSeat = seatChange.selectedSeat;
    const selectedSeatInfo = seatChange.selectedSeatInfo;
    const priceDiff = seatChange.priceDiff;
    const loading = seatChange.loading;
    const errorMessage = seatChange.errorMessage;
</script>

<style>
@keyframes successPop {
    0% { transform: scale(0); opacity: 0; }
    70% { transform: scale(1.2); }
    100% { transform: scale(1); opacity: 1; }
}
</style>
